import React, {useEffect, useRef} from "react";
// Material-UI v4 Imports
import MUIDialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
// import Divider from "@material-ui/core/Divider"; // Not strictly needed here, often implied by list style
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import CircularProgress from "@material-ui/core/CircularProgress"; // Import for loading state
import { Project } from "state/logged_user_chains";

import { ArrowForward, Close } from "@material-ui/icons";
import { withRouter, RouteComponentProps } from "react-router-dom";
import { encryptProjectId } from "../helpers/encryptUrlId";


interface ProjectSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSelectProject: (project: Project) => void;
  // NEW PROPS:
  projects: Project[] | undefined; // The list of projects fetched from the API
  projectsLoading: boolean | undefined; // Loading state flag
  projectsError: boolean | undefined; // Error state flag
  chainName: any | undefined; // Name of the chain (for dynamic title)
}

type ProjectSelectModalCombinedProps = ProjectSelectModalProps & RouteComponentProps;

const ProjectSelectModal: React.FC<ProjectSelectModalCombinedProps> = ({
  open,
  onClose,
  onSelectProject,
  // Destructure new props
  projects,
  projectsLoading,
  projectsError,
  chainName,
  history,
}) => {
  const sortedProjects = React.useMemo(() => {
    if (!projects) return undefined;

    return [...projects].sort((a, b) => {
      const getDateValue = (project: Project) => {
        const raw = (project as any).updated_at;
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? 0 : date.getTime();
      };

      return getDateValue(b) - getDateValue(a);
    });
  }, [projects]);

  const handleSelect = React.useCallback( (project: Project, roleId: number) => {
    const projectId = encryptProjectId(Number(project.project_id));
    history.push(`/projects-new/${projectId}/${roleId}`);
    const newPath = `/projects-new/${projectId}/${roleId}`;
    history.replace(newPath);
    onClose();
  },
    [history, onClose]
  );

  const paperStyles: React.CSSProperties = {
    maxWidth: "500px",
    width: "90%",
    borderRadius: "16px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.2)",
  };

  // Custom style for list items
  const listItemStyle: React.CSSProperties = {
    padding: "12px 16px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const hasAutoSelected = useRef(false);

  // Auto-select only when modal is open + projects loaded + exactly 1 project
  useEffect(() => {
    if (
      open &&
      !projectsLoading &&
      projects &&
      projects.length === 1 &&
      !hasAutoSelected.current
    ) {
      const project = projects[0];
      const roleId = chainName?.roles ? chainName.roles[0].role_id : 2;

      hasAutoSelected.current = true; // prevent multiple calls
      handleSelect(project, roleId);
    }
  }, [open, projects, projectsLoading, chainName, handleSelect]);

  // Content rendering based on loading/error state
  const renderContent = () => {
    if (projectsLoading) {
      // 1. Loading State
      return (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <CircularProgress size={32} style={{ color: "#FDF19EC2" }} />
          <Typography variant="body1" style={{ marginTop: "16px" }}>
            Loading projects for {chainName?.name || "chain"}...
          </Typography>
        </div>
      );
    }

    if (projectsError) {
      // 2. Error State
      return (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <Typography variant="body1" color="error">
            Error fetching projects. Please try again.
          </Typography>
        </div>
      );
    }

    if (!sortedProjects || sortedProjects.length === 0) {
      // 3. No Projects State
      return (
        <div style={{ padding: "32px", textAlign: "center" }}>
          <Typography variant="body1" style={{ color: "#666" }}>
            No projects found for {chainName?.name || "this chain"}.
          </Typography>
        </div>
      );
    }

    return (
      <List style={{ paddingTop: 0 }}>
        {sortedProjects.map((project) => (
          <React.Fragment key={project.project_id}>
            <ListItem button onClick={() => handleSelect(project, chainName?.roles? chainName?.roles[0].role_id:2)} style={listItemStyle}>
              <Typography
                style={{
                  fontWeight: 400,
                  margin: 0,
                  color: "#000",
                  fontSize: "16px",
                }}
              >
                {project?.chain_name} : {project?.project_name}
              </Typography>

              <ArrowForward style={{ color: "#000", width: "16px" }} />
            </ListItem>
          </React.Fragment>
        ))}
      </List>
    );
  };

  return (
    <MUIDialog
      open={open}
      onClose={onClose}
      aria-labelledby="select-project-title"
      PaperProps={{
        style: paperStyles,
      }}
      BackdropProps={{
        style: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
      }}
    >
      {/* Main Content Area */}
      <div style={{ position: "relative", padding: "16px 0" }}>
        <p
          style={{
            color: "#a0a0a0",
            position: "absolute",
            top: "-40px",
            left: "16px",
            textTransform: "uppercase",
            fontSize: "12px",
            margin: 0,
          }}
        >
          Project Selection
        </p>

        {/* Dialog Header */}
        <DialogTitle
          disableTypography
          style={{
            padding: "0 16px 8px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography style={{ fontWeight: 500, fontSize: "20px", margin: 0, color: "#000" }}>
            Select Project
          </Typography>

          <IconButton aria-label="close" onClick={onClose} style={{ color: "#000" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        {/* Render the dynamically fetched content */}
        {renderContent()}
      </div>
    </MUIDialog>
  );
};

export default withRouter(ProjectSelectModal);
